import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useUserPersistentState } from '../../hooks/useUserPersistentState';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Stack,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Autocomplete,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Close,
  QrCodeScanner,
  Delete,
  CallReceived,
  Storage,
  Business,
  Person,
  Inventory,
  Upload,
  AddBoxOutlined,
  Print,
  Autorenew,
  ArrowBack,
  Send,
  OpenInNew
} from '@mui/icons-material';
import { DataService } from '../../services/data-service';
import { LovService } from '../../services/lov-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import ExcelImportDialog, { ImportRow } from '../../components/ExcelImportDialog';
import VirtualizedMovementTable from '../../components/VirtualizedMovementTable';
import { BarcodeSearchResult, bulkBarcodeService } from '../../services/bulk-barcode-service';
import { AllPropertiesCell } from '../../components/MovementDialogs/SharedComponents';
import { LabelPaperSize, usePrint } from '../../hooks/usePrint';
import { useNavigate } from 'react-router-dom';
import { useTab } from '../../contexts/TabContext';

// Reuse interfaces from the dialog
interface StorageUnit {
  id: string;
  name: string;
  code: string;
  isLeaf: boolean;
}

interface BoxItem {
  unitId: string;
  barcode: string;
  itemName: string;
  quantity: number;
}

interface BoxDetails {
  barcode: string;
  storageUnitId: string;
  storageUnitName: string;
  items: BoxItem[];
}

interface TransferRow {
  destinationLocked: boolean;
  id: string;
  barcode: string;
  searchResult?: BarcodeSearchResult | null;
  type: 'box' | 'item';
  boxDetails?: BoxDetails;
  itemName?: string;
  unitId?: string;
  selectedDestination?: string;
  selectedBox?: string;
  quantity: number;
  loading: boolean;
  error?: string;
  unitData?: any;
  smartRecommended?: boolean;
  smartRecommendations?: any[]; // Store recommendations for scoring display
}

interface ClientSupplier {
  id: string;
  name: string;
  type: 'client' | 'supplier';
  code?: string;
  contactInfo?: string;
}

interface MoveInData {
  name: string;
  refNumber: string;
  notes: string;
  referenceId: string;
  date: string; // Date-only format (YYYY-MM-DD)
}

interface PersistentMoveInState {
  moveInData: MoveInData;
  transferRows: TransferRow[];
  selectedSource: ClientSupplier | null;
  lastUpdated: number;
}

// Box Scan Dialog Component (copied from dialog)
const BoxScanDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  boxBarcode: string;
  boxDetails: BoxDetails | null;
  onItemsConfirm: (items: BoxItem[]) => void;
  onPrintLabel?: (paperSize?: 'A4' | 'A5') => void;
}> = ({ open, onClose, boxBarcode, boxDetails, onItemsConfirm, onPrintLabel }) => {
  const [scannedItems, setScannedItems] = useState<BoxItem[]>([]);
  const [boxImportDialogOpen, setBoxImportDialogOpen] = useState(false);
  const [currentScan, setCurrentScan] = useState('');
  const [scanning, setScanning] = useState(false);
  const [printPaperSize, setPrintPaperSize] = useState<'A4' | 'A5'>('A4');
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (open && boxDetails) {
      setScannedItems([...boxDetails.items]);
    } else if (open) {
      setScannedItems([]);
    }
  }, [open, boxDetails]);

  const searchItemBarcode = async (barcode: string) => {
    if (!barcode.trim()) return;

    setScanning(true);
    try {
      const response = await DataService.get(`api/inventoryItem/search-barcode/${encodeURIComponent(barcode)}`);

      let itemName = `Unknown Unit (${barcode})`;
      let unitId = barcode;
      let isUnknown = true;

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          itemName = result.data.itemName || result.data.name || itemName;
          unitId = result.data.unitId || result.data.id || result.data.itemId || unitId;
          isUnknown = false;
        }
      }

      const existingItemIndex = scannedItems.findIndex(item => item.barcode === barcode);
      if (existingItemIndex >= 0) {
        const updatedItems = [...scannedItems];
        updatedItems[existingItemIndex].quantity += 1;
        setScannedItems(updatedItems);
      } else {
        const newItem: BoxItem = { unitId, barcode, itemName, quantity: 1 };
        setScannedItems(prev => [...prev, newItem]);
      }

      setCurrentScan('');
      dispatch(showAlert({
        message: isUnknown ? `Added unknown unit: ${barcode}. It will be created in the system.` : `Added: ${itemName}`,
        type: isUnknown ? 'warning' : 'success'
      }));
    } catch (error: any) {
      const newItem: BoxItem = {
        unitId: barcode,
        barcode,
        itemName: `Unknown Unit (${barcode})`,
        quantity: 1
      };

      const existingItemIndex = scannedItems.findIndex(item => item.barcode === barcode);
      if (existingItemIndex >= 0) {
        const updatedItems = [...scannedItems];
        updatedItems[existingItemIndex].quantity += 1;
        setScannedItems(updatedItems);
      } else {
        setScannedItems(prev => [...prev, newItem]);
      }

      setCurrentScan('');
      dispatch(showAlert({
        message: `Added unknown unit: ${barcode}. It will be created in the system.`,
        type: 'warning'
      }));
    } finally {
      setScanning(false);
    }
  };

  const handleScanSubmit = () => {
    if (currentScan.trim()) {
      searchItemBarcode(currentScan);
    }
  };

  const updateItemQuantity = (barcode: string, quantity: number) => {
    setScannedItems(prev => prev.map(item =>
      item.barcode === barcode ? { ...item, quantity: Math.max(1, quantity) } : item
    ));
  };

  const removeItem = (barcode: string) => {
    setScannedItems(prev => prev.filter(item => item.barcode !== barcode));
  };

  const handleConfirm = () => {
    if (scannedItems.length === 0) {
      dispatch(showAlert({ message: 'Please scan at least one item', type: 'warning' }));
      return;
    }
    onItemsConfirm(scannedItems);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Box Contents - {boxBarcode}</Typography>
            <IconButton onClick={onClose}><Close /></IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
              {onPrintLabel && (
                <>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Paper Size</InputLabel>
                    <Select
                      value={printPaperSize}
                      onChange={(e) => setPrintPaperSize(e.target.value as 'A4' | 'A5')}
                      label="Paper Size"
                    >
                      <MenuItem value="A4">A4</MenuItem>
                      <MenuItem value="A5">A5</MenuItem>
                    </Select>
                  </FormControl>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<Print />} 
                    onClick={() => onPrintLabel(printPaperSize)}
                  >
                    Print Label
                  </Button>
                </>
              )}
            </Stack>

            <Alert severity="info">
              <Typography variant="body2">
                Scan individual items in this box or import from Excel. Items will be automatically moved to the box's storage location.
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label="Scan Item Barcode"
              value={currentScan}
              onChange={(e) => setCurrentScan(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleScanSubmit(); }}
              InputProps={{
                startAdornment: <QrCodeScanner sx={{ mr: 1, color: 'action.active' }} />,
                endAdornment: scanning && <CircularProgress size={20} />
              }}
              disabled={scanning}
              placeholder="Scan or enter item barcode"
            />

            {scannedItems.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Barcode</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell width={100}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scannedItems.map((item) => {
                      const isUnknown = item.itemName.includes('Unknown Unit');
                      return (
                        <TableRow key={item.barcode}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">{item.barcode}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">{item.itemName}</Typography>
                              {isUnknown && (
                                <Typography variant="caption" color="warning.main">
                                  Will be created as new unit
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItemQuantity(item.barcode, parseInt(e.target.value) || 1)}
                              size="small"
                              inputProps={{ min: 1, style: { width: 60 } }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isUnknown ? 'Unknown' : 'Known'}
                              size="small"
                              color={isUnknown ? 'warning' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => removeItem(item.barcode)} color="error">
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Inventory fontSize="large" sx={{ mb: 1 }} />
                <Typography variant="body2">No items scanned yet. Start scanning item barcodes above.</Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={scannedItems.length === 0}>
            Confirm Items ({scannedItems.length})
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Generate Boxes Dialog Component (copied from dialog)
const GenerateBoxesDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onGenerate: (count: number) => void;
  loading?: boolean;
}> = ({ open, onClose, onGenerate, loading = false }) => {
  const [count, setCount] = useState('');
  const dispatch = useAppDispatch();

  const handleGenerate = () => {
    const boxCount = parseInt(count);
    if (!boxCount || boxCount <= 0 || boxCount > 100) {
      dispatch(showAlert({
        message: 'Please enter a valid number of boxes (1-100)',
        type: 'warning'
      }));
      return;
    }
    onGenerate(boxCount);
    setCount('');
    onClose();
  };

  const handleClose = () => {
    setCount('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Generate Boxes</Typography>
          <IconButton onClick={handleClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            This will generate new boxes with unique barcodes that can be scanned and managed like regular boxes.
          </Typography>

          <TextField
            fullWidth
            label="Number of Boxes"
            type="number"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            inputProps={{ min: 1, max: 100 }}
            placeholder="Enter number of boxes to generate"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter' && count) handleGenerate();
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!count || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AddBoxOutlined />}
        >
          {loading ? 'Generating...' : 'Generate Boxes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Custom details cell component with box scan dialog support
const CustomDetailsCell: React.FC<{
  row: TransferRow;
  onOpenBoxScanDialog: (row: TransferRow) => void;
}> = ({ row, onOpenBoxScanDialog }) => {
  const isBox = row.type === 'box' && (row.boxDetails || row.barcode.includes('GEN-BOX-') || row.barcode.startsWith('BOX-'));

  if (isBox) {
    const isGenerated = row.barcode.includes('GEN-BOX-');
    const isScanned = row.barcode.startsWith('BOX-') && !row.barcode.includes('GEN-BOX-');

    return (
      <Stack spacing={0.5} sx={{ py: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Storage color="primary" sx={{ fontSize: '1rem' }} />
          <Stack direction="column" spacing={0.25}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'medium', color: 'primary.main' }}>
              {isGenerated ? 'Generated Box' : 'Scanned Box'}
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onOpenBoxScanDialog(row);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              startIcon={<QrCodeScanner sx={{ fontSize: '0.75rem' }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.65rem',
                minWidth: 'auto',
                p: 0.2,
                color: 'secondary.main',
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white'
                }
              }}
            >
              Add Items
            </Button>
          </Stack>
        </Stack>
        {row.boxDetails && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {row.boxDetails.items.length} items, {row.boxDetails.items.reduce((sum, item) => sum + item.quantity, 0)} units
          </Typography>
        )}
        {row.itemName && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {row.itemName}
          </Typography>
        )}
      </Stack>
    );
  }

  // For non-box items, use the original AllPropertiesCell logic
  return <AllPropertiesCell row={row} />;
};

const NewMoveInPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { printBoxLabel, formatBoxDataForPrinting } = usePrint();
  const { registerTabCloseCallback } = useTab();

  const defaultPersistentState: PersistentMoveInState = {
    moveInData: {
      name: '',
      refNumber: '',
      notes: '',
      referenceId: '',
      date: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
    },
    transferRows: [
      {
        id: '1', barcode: '', type: 'item', quantity: 1, loading: false,
        destinationLocked: false
      }
    ],
    selectedSource: null,
    lastUpdated: Date.now()
  };

  const [persistentState, setPersistentState, clearPersistentState] = useUserPersistentState(
    'move-in-form',
    defaultPersistentState,
    { debounceMs: 1000 }
  );

  const moveInData = persistentState.moveInData;
  const transferRows = persistentState.transferRows;
  const selectedSource = persistentState.selectedSource;

  // Helper to update move in data
  const setMoveInData = (updater: (prev: MoveInData) => MoveInData | MoveInData) => {
    setPersistentState(prev => ({
      ...prev,
      moveInData: typeof updater === 'function' ? updater(prev.moveInData) : updater,
      lastUpdated: Date.now()
    }));
  };

  const setTransferRows = (updater: (prev: TransferRow[]) => TransferRow[] | TransferRow[]) => {
    setPersistentState(prev => ({
      ...prev,
      transferRows: typeof updater === 'function' ? updater(prev.transferRows) : updater,
      lastUpdated: Date.now()
    }));
  };

  const setSelectedSource = (source: ClientSupplier | null) => {
    setPersistentState(prev => ({
      ...prev,
      selectedSource: source,
      lastUpdated: Date.now()
    }));
  };
  const [clientsSuppliers, setClientsSuppliers] = useState<ClientSupplier[]>([]);
  const [storageUnits, setStorageUnits] = useState<Record<string, string>>({});
  const [availableBoxes, setAvailableBoxes] = useState<Record<string, Record<string, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingSources, setLoadingSources] = useState(false);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [destinationsLoaded, setDestinationsLoaded] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [generateBoxesDialog, setGenerateBoxesDialog] = useState(false);
  const [generatingBoxes, setGeneratingBoxes] = useState(false);


  const [boxScanDialog, setBoxScanDialog] = useState<{
    open: boolean;
    rowId: string;
    boxBarcode: string;
    boxDetails: BoxDetails | null;
  }>({ open: false, rowId: '', boxBarcode: '', boxDetails: null });

  // Smart Allocate state
  const [smartAllocateLoading, setSmartAllocateLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<any>(null);

  const handleSmartAllocate = async () => {
    const validRowsForAllocation = transferRows.filter(row =>
      row.barcode.trim() && (row.itemName || row.boxDetails) && !row.error
    );

    if (validRowsForAllocation.length === 0) {
      dispatch(showAlert({
        message: 'No valid items found for smart allocation. Please add items first.',
        type: 'warning'
      }));
      return;
    }

    setSmartAllocateLoading(true);
    try {
      // Build smart allocation payload for move-in
      const items = validRowsForAllocation.map(row => ({
        barcode: row.barcode.trim(),
        quantity: row.quantity
      }));

      const allocationPayload = {
        direction: 1,
        operationType: 'external-receipt',
        items: items,
        sourceInfo: {
          type: selectedSource?.type || 'unknown',
          id: selectedSource?.id || '',
          name: selectedSource?.name || ''
        }
      };

      const result = await DataService.post('api/InventoryAllocation/analyze', allocationPayload);

      if (!result.ok) {
        throw new Error('Smart allocation analysis failed');
      }

      const smartData = await result.json();

      if (!smartData.success) {
        throw new Error(smartData.message || 'Smart allocation analysis failed');
      }

      setRecommendations(smartData.data);

      // Auto-apply smart allocation recommendations
      await applySmartAllocationRecommendations(smartData.data);

      dispatch(showAlert({
        message: `Smart allocation completed. Found recommendations for ${smartData.data?.barcodeAllocations?.length || 0} items.`,
        type: 'success'
      }));

    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to perform smart allocation',
        type: 'error'
      }));
    } finally {
      setSmartAllocateLoading(false);
    }
  };

  const handleSelectWithoutDestination = () => {
    const rowsWithoutDestination = transferRows.filter(row =>
      row.barcode.trim() && (row.itemName || row.boxDetails) && !row.selectedDestination
    );

    const newSelection = new Set(selectedRows);
    rowsWithoutDestination.forEach(row => newSelection.add(row.id));
    setSelectedRows(newSelection);

    dispatch(showAlert({
      message: `Selected ${rowsWithoutDestination.length} items without destination.`,
      type: 'success'
    }));
  };

  const handleBulkDestinationSelection = (destinationId: string) => {
    if (selectedRows.size === 0) {
      dispatch(showAlert({
        message: 'Please select items first.',
        type: 'warning'
      }));
      return;
    }

    const selectedRowIds = Array.from(selectedRows);
    let updatedCount = 0;

    setTransferRows(prev => prev.map(row => {
      if (selectedRowIds.includes(row.id) && row.barcode.trim()) {
        updatedCount++;
        return {
          ...row,
          selectedDestination: destinationId,
          smartRecommended: false // Clear smart flag if manually set
        };
      }
      return row;
    }));

    const destinationName = storageUnits[destinationId] || destinationId;
    dispatch(showAlert({
      message: `Set destination for ${updatedCount} selected items to: ${destinationName}`,
      type: 'success'
    }));

    // Clear selection after bulk operation
    setSelectedRows(new Set());
  };

  const applySmartAllocationRecommendations = async (smartData: any) => {
    if (!smartData?.barcodeAllocations || !Array.isArray(smartData.barcodeAllocations)) {
      return;
    }

    let appliedCount = 0;
    let selectedDestinationsCount = 0;

    // Collect all row updates to apply in a single state update
    const rowUpdates: Record<string, Partial<TransferRow>> = {};

    for (const allocation of smartData.barcodeAllocations) {
      if (!allocation.success || !allocation.barcode) continue;

      // Find the corresponding row
      const rowToUpdate = transferRows.find(row =>
        row.barcode.trim() === allocation.barcode.trim()
      );

      if (!rowToUpdate) continue;

      try {
        let hasDestination = false;

        // Auto-select best destination recommendation
        if (allocation.recommendations?.length > 0) {
          // Sort recommendations by combinedScore (highest first)
          const sortedRecommendations = [...allocation.recommendations].sort(
            (a: any, b: any) => (b.combinedScore || 0) - (a.combinedScore || 0)
          );

          const bestRecommendation = sortedRecommendations[0];
          if (bestRecommendation.storageUnitId) {
            // Store the update data for this row
            rowUpdates[rowToUpdate.id] = {
              selectedDestination: bestRecommendation.storageUnitId,
              smartRecommended: true,
              smartRecommendations: allocation.recommendations || []
            };
            selectedDestinationsCount++;
            hasDestination = true;
          }
        }

        if (hasDestination) {
          appliedCount++;
        }
      } catch (error) {
        console.warn(`Failed to apply smart recommendation for ${allocation.barcode}:`, error);
      }
    }

    // Apply all row updates in a single state update
    setTransferRows(prev => prev.map(row => ({
      ...row,
      // Clear previous smart flags first
      smartRecommended: false,
      smartRecommendations: undefined,
      // Then apply updates if any
      ...(rowUpdates[row.id] || {})
    })));

    if (appliedCount > 0) {
      dispatch(showAlert({
        message: `Applied smart recommendations: ${selectedDestinationsCount} destinations auto-selected for ${appliedCount} items.`,
        type: 'success'
      }));
    }
  };

  useEffect(() => {
    loadSources();
    if (!destinationsLoaded) {
      loadDestinations();
    }
  }, []);

  // Show notification when user has saved data from previous session
  useEffect(() => {
    const hasExistingData = persistentState.lastUpdated > 0 &&
      (persistentState.moveInData.name ||
       persistentState.selectedSource ||
       persistentState.transferRows.some(row => row.barcode.trim()));

    if (hasExistingData) {
      const timeAgo = Math.round((Date.now() - persistentState.lastUpdated) / (1000 * 60)); // minutes
      const timeDisplay = timeAgo < 60
        ? `${timeAgo} minutes ago`
        : timeAgo < 1440
          ? `${Math.round(timeAgo / 60)} hours ago`
          : `${Math.round(timeAgo / 1440)} days ago`;

      dispatch(showAlert({
        message: `Restored your previous move-in progress (saved ${timeDisplay}). You can continue where you left off.`,
        type: 'info'
      }));
    }
  }, []); // Only run once on mount

  // Register tab close cleanup callback
  useEffect(() => {
    registerTabCloseCallback('/movements/new-move-in', () => {
      clearPersistentState();
    });
    // Note: We don't unregister on unmount - we want the callback to persist
    // so it runs when the tab is closed from the tab system
  }, [registerTabCloseCallback, clearPersistentState]);

  // Copy all functions from the dialog
  const loadSources = async () => {
    setLoadingSources(true);
    try {
      const [clients, suppliers] = await Promise.all([
        LovService.getLovDictionary('all-clients'),
        LovService.getLovDictionary('all-suppliers')
      ]);

      const combinedList: ClientSupplier[] = [
        ...Object.entries(clients || {}).map(([id, name]) => ({
          id,
          name: String(name),
          type: 'client' as const,
          code: id,
          contactInfo: 'Client'
        })),
        ...Object.entries(suppliers || {}).map(([id, name]) => ({
          id,
          name: String(name),
          type: 'supplier' as const,
          code: id,
          contactInfo: 'Supplier'
        }))
      ];

      setClientsSuppliers(combinedList);
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Failed to load clients and suppliers', type: 'warning' }));
      setClientsSuppliers([]);
    } finally {
      setLoadingSources(false);
    }
  };

  const loadDestinations = async () => {
    if (loadingDestinations || destinationsLoaded) return;

    setLoadingDestinations(true);
    try {
      const units = await LovService.getLovDictionary('leaf-storage-units');
      setStorageUnits(units || {});
      setDestinationsLoaded(true);
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Failed to load storage units', type: 'warning' }));
      setStorageUnits({});
    } finally {
      setLoadingDestinations(false);
    }
  };

  const loadBoxesForStorageUnit = async (storageUnitId: string) => {
    try {
      const boxes = await LovService.getLovDictionary(`${storageUnitId}/boxes`);
      setAvailableBoxes(prev => ({
        ...prev,
        [storageUnitId]: boxes || {}
      }));
      return boxes || {};
    } catch (error: any) {
      console.warn(`Failed to load boxes for storage unit ${storageUnitId}:`, error);
      setAvailableBoxes(prev => ({
        ...prev,
        [storageUnitId]: {}
      }));
      return {};
    }
  };

  // Copy all the core functions from the dialog (searchBarcode, handleBarcodeChange, etc.)
  const searchBarcode = async (rowId: string, barcode: string) => {
    if (!barcode.trim()) return;

    setTransferRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, loading: true, error: undefined } : row
    ));

    try {
      if (barcode.includes('GEN-BOX-')) {
        const existingGeneratedBox = transferRows.find(row =>
          row.barcode === barcode && row.type === 'box'
        );

        if (existingGeneratedBox) {
          setTransferRows(prev => prev.map(row =>
            row.id === rowId
              ? {
                  ...row,
                  loading: false,
                  type: 'box',
                  boxDetails: existingGeneratedBox.boxDetails
                }
              : row
          ));

          setBoxScanDialog({
            open: true,
            rowId,
            boxBarcode: barcode,
            boxDetails: existingGeneratedBox.boxDetails
          });
          return;
        }
      }

      const response = await DataService.get(`api/inventoryItem/search-barcode/${encodeURIComponent(barcode)}`);

      if (response.ok) {
        const searchResult = await response.json();
        if (searchResult.success && searchResult.data) {
          if (searchResult.data.type === 'box') {
            // For existing boxes, use the destination from the API response
            const boxLocation = searchResult.data.locations?.[0];
            const destinationFromApi = boxLocation?.storageUnitId;

            setTransferRows(prev => prev.map(row =>
              row.id === rowId
                ? {
                    ...row,
                    loading: false,
                    type: 'box',
                    itemName: searchResult.data.itemName || `Existing Box (${barcode})`,
                    unitId: searchResult.data.unitId || barcode,
                    selectedDestination: destinationFromApi,
                    unitData: searchResult.data,
                    isExistingBox: true, // Flag to indicate this is an existing box
                    destinationLocked: true // Flag to prevent destination changes
                  }
                : row
            ));
            return;
          } else {
            const storageUnitEntries = Object.entries(storageUnits);
            const autoDestination = storageUnitEntries.length === 1 ? storageUnitEntries[0][0] : undefined;

            setTransferRows(prev => prev.map(row =>
              row.id === rowId
                ? {
                    ...row,
                    loading: false,
                    type: 'item',
                    itemName: searchResult.data.itemName || 'Unknown Item',
                    unitId: searchResult.data.unitId || searchResult.data.id || searchResult.data.itemId,
                    selectedDestination: autoDestination,
                    unitData: searchResult.data
                  }
                : row
            ));
            return;
          }
        }
      }

      const storageUnitEntries = Object.entries(storageUnits);
      const autoDestination = storageUnitEntries.length === 1 ? storageUnitEntries[0][0] : undefined;

      const isBoxLike = barcode.startsWith('BOX-') || barcode.toLowerCase().includes('box');
      const itemType = isBoxLike ? 'box' : 'item';
      const itemName = isBoxLike ? `Unknown Box (${barcode})` : `Unknown Unit (${barcode})`;
      const warningMessage = isBoxLike
        ? 'Box not found in system. It will be created as an unknown box.'
        : 'Unit not found in system. It will be created as an unknown unit.';

      setTransferRows(prev => prev.map(row =>
        row.id === rowId
          ? {
              ...row,
              loading: false,
              type: itemType,
              itemName,
              unitId: barcode,
              selectedDestination: autoDestination,
              error: 'This item is not in the system and will be created as unknown'
            }
          : row
      ));
      dispatch(showAlert({
        message: warningMessage,
        type: 'warning'
      }));
    } catch (error: any) {
      setTransferRows(prev => prev.map(row =>
        row.id === rowId ? { ...row, loading: false, error: error.message } : row
      ));
      dispatch(showAlert({ message: error.message || 'Failed to search barcode', type: 'error' }));
    }
  };

  const handleBarcodeChange = (rowId: string, barcode: string) => {
    setTransferRows(prev => prev.map(row =>
      row.id === rowId
        ? {
            ...row,
            barcode,
            type: 'item',
            boxDetails: undefined,
            itemName: undefined,
            unitId: undefined,
            selectedDestination: undefined,
            selectedBox: undefined
          }
        : row
    ));
  };

  const handleBarcodeSubmit = (rowId: string) => {
    const row = transferRows.find(r => r.id === rowId);
    if (row && row.barcode.trim()) {
      const existingRowIndex = transferRows.findIndex(r =>
        r.id !== rowId &&
        r.barcode === row.barcode &&
        r.selectedDestination === row.selectedDestination &&
        r.selectedDestination !== undefined
      );

      if (existingRowIndex >= 0) {
        const existingRow = transferRows[existingRowIndex];
        updateRowField(existingRow.id, 'quantity', existingRow.quantity + 1);

        setTransferRows(prev => prev.map(r =>
          r.id === rowId
            ? {
                ...r,
                barcode: '',
                type: 'item',
                itemName: undefined,
                unitId: undefined,
                selectedDestination: undefined,
                selectedBox: undefined,
                quantity: 1
              }
            : r
        ));

        dispatch(showAlert({
          message: 'Increased quantity of existing item with same destination',
          type: 'info'
        }));
        return;
      }

      searchBarcode(rowId, row.barcode);

      const currentIndex = transferRows.findIndex(r => r.id === rowId);
      if (currentIndex === transferRows.length - 1) {
        addNewRow();
      }
    }
  };

  const addNewRow = () => {
    const newId = Date.now().toString();
    setTransferRows(prev => [...prev, {
      id: newId,
      barcode: '',
      type: 'item',
      quantity: 1,
      loading: false,
      destinationLocked: false
    }]);
  };

  const removeRow = (rowId: string) => {
    if (transferRows.length === 1) return;
    setTransferRows(prev => prev.filter(row => row.id !== rowId));
  };

  const canMoveIn = useMemo(() => {
    const hasSource = !!selectedSource;
    const validRows = transferRows.filter(row => row.barcode.trim());
    const readyRows = validRows.filter(row => row.selectedDestination);
    return !submitting && hasSource && validRows.length > 0 && readyRows.length > 0;
  }, [submitting, selectedSource, transferRows]);

  const statusCounts = useMemo(() => {
    const totalRows = transferRows.length;
    const withBarcodes = transferRows.filter(row => row.barcode.trim()).length;
    const withDestinations = transferRows.filter(row => row.barcode.trim() && row.selectedDestination).length;
    const readyToMove = transferRows.filter(row => row.barcode.trim() && row.selectedDestination).length;
    const missingDestination = transferRows.filter(row => row.barcode.trim() && !row.selectedDestination).length;
    const boxes = transferRows.filter(row => row.type === 'box' && row.barcode.trim()).length;
    const items = transferRows.filter(row => row.type === 'item' && row.barcode.trim()).length;
    const errors = transferRows.filter(row => row.error).length;

    return {
      totalRows,
      withBarcodes,
      withDestinations,
      readyToMove,
      missingDestination,
      boxes,
      items,
      errors,
      completion: withBarcodes > 0 ? Math.round((readyToMove / withBarcodes) * 100) : 0
    };
  }, [transferRows]);

  const updateRowField = (rowId: string, field: keyof TransferRow, value: any) => {
    setTransferRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, [field]: value, ...(field === 'selectedDestination' ? { selectedBox: undefined } : {}) } : row
    ));

    if (field === 'selectedDestination' && value) {
      loadBoxesForStorageUnit(value);
    }

    if (field === 'selectedDestination') {
      const currentRow = transferRows.find(r => r.id === rowId);
      if (currentRow && currentRow.barcode.trim() && currentRow.type === 'item') {
        const duplicateRowIndex = transferRows.findIndex(r =>
          r.id !== rowId &&
          r.barcode === currentRow.barcode &&
          r.selectedDestination === value &&
          value !== undefined
        );

        if (duplicateRowIndex >= 0) {
          const duplicateRow = transferRows[duplicateRowIndex];
          setTimeout(() => {
            updateRowField(duplicateRow.id, 'quantity', duplicateRow.quantity + (currentRow.quantity || 1));

            setTransferRows(prev => {
              if (prev.length <= 1) {
                return prev.map(r => r.id === rowId ? {
                  id: r.id,
                  barcode: '',
                  type: 'item',
                  quantity: 1,
                  loading: false,
                  itemName: undefined,
                  unitId: undefined,
                  selectedDestination: undefined,
                  selectedBox: undefined,
                  destinationLocked: false
                } : r);
              }
              return prev.filter(r => r.id !== rowId);
            });

            dispatch(showAlert({
              message: 'Merged duplicate items with same barcode and destination',
              type: 'info'
            }));
          }, 50);
        }
      }
    }
  };

  const handleBoxItemsConfirm = (items: BoxItem[]) => {
    const { rowId, boxBarcode } = boxScanDialog;
    setTransferRows(prev => prev.map(row =>
      row.id === rowId
        ? {
            ...row,
            boxDetails: row.boxDetails
              ? { ...row.boxDetails, items }
              : {
                  barcode: boxBarcode,
                  storageUnitId: row.selectedDestination || '',
                  storageUnitName: row.selectedDestination ? (storageUnits[row.selectedDestination] || row.selectedDestination) : '',
                  items
                }
          }
        : row
    ));
    setBoxScanDialog({ open: false, rowId: '', boxBarcode: '', boxDetails: null });
  };

  const generateBoxes = (count: number) => {
    setGeneratingBoxes(true);

    try {
      const generatedBoxes: TransferRow[] = [];
      const timestamp = Date.now();

      for (let i = 1; i <= count; i++) {
        const boxBarcode = `GEN-BOX-${timestamp}-${String(i).padStart(3, '0')}`;

        generatedBoxes.push({
          id: `gen-${timestamp}-${i}`,
          barcode: boxBarcode,
          type: 'box',
          quantity: 1,
          loading: false,
          boxDetails: {
            barcode: boxBarcode,
            storageUnitId: '',
            storageUnitName: 'Generated Box',
            items: []
          },
          destinationLocked: false
        });
      }

      setTransferRows(prev => {
        const existing = prev.filter(x => x.barcode.trim());
        return [
          ...existing,
          ...generatedBoxes,
          {
            id: Date.now().toString(),
            barcode: '',
            type: 'item',
            quantity: 1,
            loading: false,
            destinationLocked: false
          }
        ];
      });

      dispatch(showAlert({
        message: `Generated ${count} boxes successfully. They can now be scanned and filled with items.`,
        type: 'success'
      }));

    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to generate boxes',
        type: 'error'
      }));
    } finally {
      setGeneratingBoxes(false);
    }
  };

  const handleRowSelection = useCallback((rowId: string, selected: boolean) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (selected) {
        newSelection.add(rowId);
      } else {
        newSelection.delete(rowId);
      }
      return newSelection;
    });
  }, []);

  const handlePrintBoxLabel = async (boxBarcode: string, paperSize: 'A4' | 'A5' = 'A4') => {
    const printOptions = formatBoxDataForPrinting({
      barcode: boxBarcode,
      labelPaperSize: 'SMALL_5x2_5' as LabelPaperSize,
      additionalFields: {
        createdDate: new Date().toLocaleDateString(),
        notes: 'Move In Box',
        clientName: selectedSource?.name,
        supplierName: selectedSource?.type === 'supplier' ? selectedSource.name : undefined
      }
    });

    await printBoxLabel(printOptions);
  };

  // Tab 0: Source & Info
  // Render operation information form
  const renderOperationInfo = () => (
    <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={2.5}>
          <TextField
            label="Operation Name *"
            value={moveInData.name}
            onChange={(e) => setMoveInData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Quarterly Move In"
            fullWidth
            required
            error={!moveInData.name.trim()}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Date *"
            type="date"
            value={moveInData.date}
            onChange={(e) => setMoveInData(prev => ({ ...prev, date: e.target.value }))}
            fullWidth
            required
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={2.5}>
          <TextField
            label="Reference Number"
            value={moveInData.refNumber}
            onChange={(e) => setMoveInData(prev => ({ ...prev, refNumber: e.target.value }))}
            placeholder="MOVEIN-001"
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            label="Notes"
            value={moveInData.notes}
            onChange={(e) => setMoveInData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Additional notes"
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            options={clientsSuppliers}
            getOptionLabel={(option) =>
              typeof option === 'string'
                ? option
                : `${option.name} (${option.type.toUpperCase()})`
            }
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                  {option.type === 'client' ? <Person fontSize="small" /> : <Business fontSize="small" />}
                  <Box>
                    <Typography variant="body2" fontWeight="medium">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.type.toUpperCase()} {option.code && `• ${option.code}`}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
            value={selectedSource}
            onChange={(_, newValue) =>
              setSelectedSource(typeof newValue === 'object' && newValue !== null ? newValue as ClientSupplier : null)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Client or Supplier *"
                placeholder="Choose source..."
                disabled={submitting || loadingSources}
                required
                error={!selectedSource}
                size="small"
              />
            )}
            loading={loadingSources}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderOperationInfo_old = () => (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Move In Information_old
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select the source (client or supplier) and enter additional information for this move in operation
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Notes_old"
                value={moveInData.notes}
                onChange={(e) => setMoveInData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes or comments about this move in operation"
                multiline
                rows={4}
                fullWidth
              />
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );

  // Tab 1: Items to Move In (full table functionality)
  // Render items to move in
  const renderItemsToMoveIn = () => (
    <Stack spacing={2}>
      {/* Status Overview */}
      {/* <Card variant="outlined">
        <CardContent sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <CallReceived color="success" />
              <Typography variant="h6">Items to Move In</Typography>
              <Chip label="External Receipt" size="small" color="success" />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`📋 ${statusCounts.totalRows} Total`} size="small" color="default" variant="outlined" />
              <Chip label={`📦 ${statusCounts.withBarcodes} Items`} size="small" color="primary" variant="outlined" />
              <Chip label={`✅ ${statusCounts.readyToMove} Ready`} size="small" color="success" variant={statusCounts.readyToMove > 0 ? "filled" : "outlined"} />
              {statusCounts.missingDestination > 0 && (
                <Chip label={`⚠️ ${statusCounts.missingDestination} Need Destination`} size="small" color="warning" variant="filled" />
              )}
              <Chip label={`${statusCounts.completion}% Complete`} size="small" color={statusCounts.completion === 100 ? "success" : statusCounts.completion >= 50 ? "warning" : "default"} variant="outlined" />
            </Stack>
          </Stack>
        </CardContent>
      </Card> */}

      {/* Action Buttons */}
      <Card variant="outlined">
        <CardContent sx={{ p: 1.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Actions:
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setImportDialogOpen(true)}
                startIcon={<Upload />}
                disabled={submitting || importing}
              >
                {importing ? 'Importing...' : 'Import Excel'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setGenerateBoxesDialog(true)}
                startIcon={<AddBoxOutlined />}
                color="primary"
                disabled={submitting || generatingBoxes}
              >
                {generatingBoxes ? 'Generating...' : 'Generate Boxes'}
              </Button>
              {/* <Button
                size="small"
                variant="outlined"
                onClick={handleSmartAllocate}
                startIcon={smartAllocateLoading ? <CircularProgress size={16} /> : <Autorenew />}
                color="secondary"
                disabled={submitting || smartAllocateLoading || transferRows.filter(r => r.barcode.trim()).length === 0}
              >
                {smartAllocateLoading ? 'Analyzing...' : 'Smart Allocation'}
              </Button> */}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {selectedRows.size > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Bulk Actions:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Set Destination</InputLabel>
                    <Select
                      value=""
                      onChange={(e) => handleBulkDestinationSelection(e.target.value)}
                      label="Set Destination"
                      disabled={submitting}
                    >
                      {Object.entries(storageUnits).map(([id, name]) => (
                        <MenuItem key={id} value={id}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Storage fontSize="small" />
                            <Typography variant="caption">{name}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
              <Button
                size="small"
                variant="text"
                onClick={handleSelectWithoutDestination}
                disabled={submitting}
                sx={{ minWidth: 'auto' }}
              >
                Select Missing Destinations ({transferRows.filter(r => r.barcode.trim() && (r.itemName || r.boxDetails) && !r.selectedDestination).length})
              </Button>
            </Stack>
            <Chip
              label={`${selectedRows.size} Selected`}
              size="small"
              color={selectedRows.size > 0 ? "primary" : "default"}
              variant="outlined"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Main Table */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%', minHeight: '500px' }}>
        <VirtualizedMovementTable
          data={transferRows}
          itemsPerPage={20}
          height="auto"
          showPagination={true}
          emptyMessage="Start scanning barcodes to add items"
          dense={true}
          rowHeight="compact"
          ensureEmptyRow={true}
          onAddNewRow={addNewRow}
          columns={[
            {
              field: 'select',
              headerName: '☐',
              width: 50,
              renderCell: (row: TransferRow) => {
                const isSelectable = row.barcode.trim() && (row.itemName || row.boxDetails);
                return (
                  <Checkbox
                    checked={selectedRows.has(row.id)}
                    onChange={(e) => handleRowSelection(row.id, e.target.checked)}
                    disabled={!isSelectable || submitting}
                    size="small"
                  />
                );
              }
            },
            {
              field: 'barcode',
              headerName: 'Barcode',
              width: 140,
              renderCell: (row: TransferRow) => (
                <TextField
                  value={row.barcode || ''}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleBarcodeChange(row.id, e.target.value);
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') handleBarcodeSubmit(row.id);
                  }}
                  onBlur={() => handleBarcodeSubmit(row.id)}
                  onFocus={(e) => e.stopPropagation()}
                  placeholder="Scan barcode"
                  size="small"
                  fullWidth
                  disabled={submitting}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <QrCodeScanner sx={{ mr: 0.5, color: 'action.active', fontSize: '1rem' }} />,
                    endAdornment: row.loading && <CircularProgress size={16} />
                  }}
                  error={!!row.error}
                  sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 } }}
                />
              )
            },
            {
              field: 'details',
              headerName: 'Details',
              width: 220,
              renderCell: (row: TransferRow) => (
                <CustomDetailsCell
                  row={row}
                  onOpenBoxScanDialog={(row) => {
                    setBoxScanDialog({
                      open: true,
                      rowId: row.id,
                      boxBarcode: row.barcode,
                      boxDetails: row.boxDetails || null
                    });
                  }}
                />
              )
            },
            {
              field: 'destination',
              headerName: 'Destination',
              width: 200,
              renderCell: (row: TransferRow) => (
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.8rem' }}>Destination</InputLabel>
                  <Select
                    value={row.selectedDestination || ''}
                    onChange={(e) => {
                      updateRowField(row.id, 'selectedDestination', e.target.value);
                      updateRowField(row.id, 'smartRecommended', false);
                    }}
                    label="Destination"
                    disabled={submitting || loadingDestinations || row.destinationLocked}
                    sx={{
                      minWidth: 160,
                      fontSize: '0.8rem',
                      ...(row.smartRecommended && {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#9c27b0',
                          borderWidth: '2px'
                        }
                      }),
                      ...(row.destinationLocked && {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ff9800',
                          borderWidth: '2px'
                        },
                        '& .MuiSelect-select': {
                          backgroundColor: '#fff3e0',
                          color: '#e65100'
                        }
                      })
                    }}
                    renderValue={(selected) => {
                      if (!selected) return '';
                      return storageUnits[selected] || selected;
                    }}
                  >
                    {Object.entries(storageUnits).map(([id, name]) => {
                      // Check if this destination has a recommendation score
                      const recommendation = row.smartRecommendations?.find((rec: any) =>
                        rec.storageUnitId === id
                      );

                      return (
                        <MenuItem key={id} value={id} sx={{ fontSize: '0.8rem' }}>
                          <Stack direction="row" alignItems="center" spacing={1} width="100%">
                            <Storage fontSize="small" sx={{ fontSize: '0.9rem' }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="caption">{name}</Typography>
                              {recommendation && (
                                <Typography variant="caption" color="primary" sx={{ display: 'block', fontSize: '0.65rem', fontWeight: 'bold' }} title={recommendation.recommendationReason || 'Smart allocation recommendation'}>
                                  Score: {Math.round(recommendation.combinedScore || 0)}%
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )
            },
            {
              field: 'selectedBox',
              headerName: 'Container',
              width: 160,
              renderCell: (row: TransferRow) => {
                if (row.type === 'box') {
                  return (
                    <Chip
                      label="Whole Box"
                      size="small"
                      variant="outlined"
                      color="primary"
                      icon={<Storage sx={{ fontSize: '0.9rem' }} />}
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  );
                }

                if (!row.selectedDestination) {
                  return <Typography variant="caption" color="text.secondary">-</Typography>;
                }

                const boxes = availableBoxes[row.selectedDestination] || {};
                const boxEntries = Object.entries(boxes);

                if (boxEntries.length === 0) {
                  return (
                    <Chip
                      label="Independent"
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  );
                }

                const allOptions = [
                  { value: '', label: 'Independent' },
                  ...boxEntries.map(([barcode, name]) => ({ value: barcode, label: name }))
                ];

                return (
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.8rem' }}>Container</InputLabel>
                    <Select
                      value={row.selectedBox || ''}
                      onChange={(e) => updateRowField(row.id, 'selectedBox', e.target.value)}
                      label="Container"
                      disabled={submitting}
                      sx={{ minWidth: 140, fontSize: '0.8rem' }}
                    >
                      {allOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.8rem' }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            {option.value === '' ? (
                              <Inventory fontSize="small" color="action" sx={{ fontSize: '0.9rem' }} />
                            ) : (
                              <Storage fontSize="small" sx={{ fontSize: '0.9rem' }} />
                            )}
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{option.label}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }
            },
            {
              field: 'quantity',
              headerName: 'Qty',
              width: 90,
              renderCell: (row: TransferRow) => {
                if (row.type === 'box' && row.boxDetails) {
                  return (
                    <Chip
                      label={`${row.boxDetails.items.reduce((sum, item) => sum + item.quantity, 0)}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20, minWidth: 40 }}
                    />
                  );
                } else if (row.type === 'item') {
                  return (
                    <TextField
                      type="number"
                      value={row.quantity}
                      onChange={(e) => updateRowField(row.id, 'quantity', parseInt(e.target.value) || 1)}
                      size="small"
                      inputProps={{ min: 1, style: { width: 60, fontSize: '0.8rem' } }}
                      disabled={submitting}
                      sx={{ '& .MuiInputBasfe-input': { py: 0.5 } }}
                    />
                  );
                }
                return <Typography variant="caption" color="text.secondary">-</Typography>;
              }
            },
            {
              field: 'actions',
              headerName: '',
              width: 60,
              renderCell: (row: TransferRow) => (
                <Stack direction="row" spacing={0.5}>
                  {transferRows.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeRow(row.id)}
                      disabled={submitting}
                      color="error"
                      sx={{ p: 0.5 }}
                    >
                      <Delete sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  )}
                </Stack>
              )
            }
          ]}
        />
      </Box>
    </Stack>
  );

  const validateMoveIn = (): string[] => {
    const errors: string[] = [];

    if (!moveInData.name.trim()) {
      errors.push('Operation name is required');
    }

    if (!selectedSource) {
      errors.push('Please select a client or supplier source');
    }

    const allRows = transferRows.slice(0, -1).concat(
      transferRows.slice(-1).filter(row => row.barcode.trim())
    );
    const validRows = allRows.filter(row => row.barcode.trim());

    if (validRows.length === 0) {
      errors.push('At least one item must be scanned');
    }

    validRows.forEach((row, index) => {
      if (row.type === 'item') {
        if (!row.selectedDestination) {
          errors.push(`Row ${index + 1}: Destination storage unit not selected`);
        }
        if (row.quantity <= 0) {
          errors.push(`Row ${index + 1}: Quantity must be greater than 0`);
        }
      }

      if (row.type === 'box') {
        if (!row.selectedDestination) {
          errors.push(`Row ${index + 1}: Destination storage unit not selected for box`);
        }
        if (!row.boxDetails || row.boxDetails.items.length === 0) {
          errors.push(`Row ${index + 1}: Box must contain scanned items`);
        }
      }
    });

    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateMoveIn();
    if (validationErrors.length > 0) {
      dispatch(showAlert({ message: validationErrors.join('\n'), type: 'warning' }));
      return;
    }

    setSubmitting(true);
    try {
      const allRows = transferRows.slice(0, -1).concat(
        transferRows.slice(-1).filter(row => row.barcode.trim())
      );
      const validRows = allRows.filter(row => row.barcode.trim());

      const units: any[] = [];

      validRows.forEach(row => {
        if (row.type === 'box' && row.boxDetails) {
          const isGeneratedBox = row.barcode.includes('GEN-BOX-');

          if (isGeneratedBox) {
            // For generated boxes, add all items with the box barcode (without GEN- prefix) as destinationBoxRefNumber
            const boxRefNumber = row.barcode.replace('GEN-BOX-', 'BOX-');
            row.boxDetails.items.forEach(item => {
              units.push({
                barcode: item.barcode,
                quantity: item.quantity,
                destinationStorageUnitId: row.selectedDestination!,
                destinationBoxRefNumber: boxRefNumber,
                notes: `Generated box item: ${item.itemName || item.barcode}`
              });
            });
          } else {
            // For existing boxes (scanned boxes), add each item individually with the box barcode as destinationBoxRefNumber
            const boxRefNumber = row.barcode; // Use the existing box barcode as the destination box reference
            row.boxDetails.items.forEach(item => {
              units.push({
                barcode: item.barcode,
                quantity: item.quantity,
                destinationStorageUnitId: row.selectedDestination!,
                destinationBoxRefNumber: boxRefNumber,
                notes: `Existing box item: ${item.itemName || item.barcode}`
              });
            });
          }
        } else {
          // Regular items
          units.push({
            barcode: row.barcode,
            quantity: row.quantity,
            destinationStorageUnitId: row.selectedDestination!,
            destinationBoxRefNumber: row.selectedBox && row.selectedBox.trim() !== '' ? row.selectedBox : null,
            notes: row.itemName ? `Item: ${row.itemName}` : null
          });
        }
      });

      const payload = {
        sourceType: selectedSource!.type === 'client' ? 4 : 3,
        sourceId: selectedSource!.id,
        units,
        name: moveInData.name.trim() || `Move In from ${selectedSource!.name}`,
        refNumber: moveInData.refNumber.trim() || undefined,
        notes: moveInData.notes.trim() || undefined,
        operationDate: moveInData.date ? `${moveInData.date}T00:00:00` : undefined
      };

      const response = await DataService.post('api/Operation/move-in', payload);

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || 'Failed to submit move in');
      }

      const result = await response.json();

      dispatch(showAlert({
        message: `Successfully moved in ${units.length} units from ${selectedSource!.name}`,
        type: 'success'
      }));

      // Clear the persistent state after successful submission
      clearPersistentState();

      navigate('/movements/move-in');
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Failed to submit move in', type: 'error' }));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = moveInData.name.trim() !== '' && !!selectedSource && statusCounts.readyToMove > 0;

  // Excel import handler
  const handleImport = async (importRows: ImportRow[]) => {
    if (!importRows.length) return;

    try {
      const qtyByBarcode = new Map<string, number>();
      for (const row of importRows) {
        const b = (row.barcode || '').trim();
        if (!b) continue;
        const q = Number(row.quantity) || 0;
        qtyByBarcode.set(b, (qtyByBarcode.get(b) || 0) + q);
      }
      const barcodes = [...qtyByBarcode.keys()];
      if (barcodes.length === 0) return;

      const results = await bulkBarcodeService.searchBarcodes(barcodes, {
        includeLocations: false, // Move in doesn't need existing locations
        includeBoxes: false,
      });

      const foundItems: TransferRow[] = [];
      let ignoredUnknown = 0;
      let ignoredAlreadyInInventory = 0;

      let currentId = Date.now();

      for (const r of results) {
        const qty = qtyByBarcode.get(r.barcode) ?? 0;

        if (!r.found) {
          ignoredUnknown++;
          continue;
        }

        // For move in, we want items that are known but preferably not in inventory
        if (r.locations && r.locations.length > 0) {
          // Item is already in inventory, but we can still allow move in
          ignoredAlreadyInInventory++;
        }

        const searchResult: BarcodeSearchResult = {
          type: r.type === 'box' ? 'box' : 'unit',
          itemName: r.itemName || '',
          unitId: r.unitId,
          locations: r.locations || [], // Empty for move in
          unit: r.unit
        };

        foundItems.push({
          id: (currentId++).toString(),
          barcode: r.barcode,
          searchResult,
          type: r.type === 'box' ? 'box' : 'item', // Use 'item' instead of 'unit'
          itemName: r.itemName,
          unitId: r.unitId,
          quantity: Math.max(1, qty || 1),
          loading: false,
          unitData: r.unit,
          destinationLocked: false
        });
      }

      setTransferRows(prev => {
        const existing = prev.filter(row => row.barcode.trim());
        return [
          ...existing,
          ...foundItems,
          {
            id: (Date.now() + foundItems.length).toString(),
            barcode: '',
            searchResult: null,
            quantity: 1,
            loading: false,
            type: 'box',
            destinationLocked: false
          },
        ];
      });

      const importedCount = foundItems.length;
      const msg =
        importedCount > 0
          ? ignoredAlreadyInInventory > 0
            ? `Imported ${importedCount} item(s). ${ignoredAlreadyInInventory} item(s) already in inventory. ${ignoredUnknown} unknown item(s) ignored.`
            : ignoredUnknown > 0
              ? `Imported ${importedCount} item(s). ${ignoredUnknown} unknown item(s) ignored.`
              : `Imported ${importedCount} item(s).`
          : `No items imported. ${ignoredUnknown} unknown item(s) ignored.`;

      dispatch(showAlert({
        message: msg,
        type: importedCount > 0 ? 'success' : 'warning',
      }));

      setImportDialogOpen(false);
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to import items',
        type: 'error',
      }));
      setImportDialogOpen(false);
      throw error;
    }
  };

  // Cancel handler with form reset
  const handleCancel = () => {
    clearPersistentState();
    navigate('/movements/move-in');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Unified Header with Operation Info, Stats, and Actions */}
      <Paper sx={{ borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
        {/* Top Header Bar */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/movements/move-in')} aria-label="ArrowBack">
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  New Move In
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Move in items from clients or suppliers into storage units
                  </Typography>
                  {persistentState.lastUpdated > 0 && (
                    <Chip
                      label={`Auto-saved: ${new Date(persistentState.lastUpdated).toLocaleTimeString()}`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
                startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
              >
                {submitting ? 'Processing...' : 'Execute Move In'}
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Operation Info Row */}
        {renderOperationInfo()}
      </Paper>

      {/* Content - Single scroll area */}
      <Box sx={{ flex: 1, p: 3 }}>
        {renderItemsToMoveIn()}
      </Box>

      {/* All Dialogs */}
      <BoxScanDialog
        open={boxScanDialog.open}
        onClose={() => setBoxScanDialog({ open: false, rowId: '', boxBarcode: '', boxDetails: null })}
        boxBarcode={boxScanDialog.boxBarcode}
        boxDetails={boxScanDialog.boxDetails}
        onItemsConfirm={handleBoxItemsConfirm}
        onPrintLabel={(paperSize) => handlePrintBoxLabel(boxScanDialog.boxBarcode, paperSize)}
      />

      <GenerateBoxesDialog
        open={generateBoxesDialog}
        onClose={() => setGenerateBoxesDialog(false)}
        onGenerate={generateBoxes}
        loading={generatingBoxes}
      />

      <ExcelImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImport}
        title="Import Items for Move In"
        expectedColumns={{
          required: ['barcode', 'quantity'],
          optional: ['destination']
        }}
      />

    </Box>
  );
};

export default NewMoveInPage;